<template>
  <div>
    <v-snackbar v-model="copied" color="success">
      Dato copiado al portapapeles.
    </v-snackbar>

    <v-dialog v-model="deleting" width="500">
      <v-card>
        <v-card-title>
          Deshabilitar resultado
          <v-spacer />
          <v-btn icon small @click="deleting = false">
            <v-icon>
              mdi-close
            </v-icon>
          </v-btn>
        </v-card-title>

        <v-card-text class="text--primary">
          Al marcar un resultado como "No le interesa" lo deshabilitará del
          listado de conexiones, y lo posicionará al final del mismo (sin
          borrarlo definitivamente). ¿Desea continuar?
        </v-card-text>

        <v-card-actions>
          <v-spacer />

          <v-btn
            small
            color="primary"
            outlined
            rounded="pill"
            @click="deleting = false"
          >
            Cancelar
          </v-btn>

          <v-btn small color="error" rounded="pill" @click="disableResult">
            Deshabilitar
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <show-results ref="show" :search="search"></show-results>

    <v-card
      :disabled="result && result.dropped"
      :ripple="false"
      tile
      flat
      class="rounded-t-lg"
    >
      <v-icon
        style="position: absolute; top: -4px; right: -4px; z-index: 3"
        v-if="search.unseenResults"
        color="primary"
      >
        mdi-bell-circle
      </v-icon>

      <v-card-title
        v-if="search.user"
        class="text-subtitle-2 py-2 grey lighten-3"
      >
        <div>
          <div v-if="showHead">
            A nombre de
            <span class="primary--text">{{ search.user.profile.name }}</span
            ><span v-if="search.user.profile.email"
              >, {{ search.user.profile.email }}</span
            ><span v-if="search.user.profile.phone"
              >, {{ search.user.profile.phone }}</span
            >
          </div>

          <div class="caption" style="width: fit-content">
            Fecha de creación:
            {{ new Date(search.createdAt).toDateString() }}
          </div>
        </div>
        <v-spacer></v-spacer>

        <v-select
          v-model="state"
          :items="states"
          :label="result ? 'Estado de la conexión' : 'Estado de la búsqueda'"
          outlined
          dense
          hide-details
          class="ml-auto mr-1 rounded-pill"
          style="max-width: 230px"
          @change="updateState"
        ></v-select>

        <v-btn
          small
          outlined
          class="rounded-xl"
          color="primary"
          :to="{ name: 'admin_user_detail', params: { id: search.user.id } }"
          v-if="showHead"
        >
          Ver cliente
        </v-btn>
      </v-card-title>

      <v-divider v-if="search.user"></v-divider>

      <v-card-text @click="openShow" style="cursor: pointer" class="pa-0">
        <v-card
          outlined
          rounded="lg"
          :class="{ 'rounded-t-0': search.user }"
          :style="{ marginTop: search.user ? '-1px' : '' }"
        >
          <v-card-text>
            <div class="d-flex justify-space-between mb-2 align-center">
              <div class="text-subtitle-1">
                Búsqueda en
                <span class="font-weight-medium primary--text">{{
                  search.address || "todo el país"
                }}</span>
              </div>

              <div>
                <template v-for="propertyType in search.propertyType">
                  <v-chip
                    small
                    color="secondary"
                    class="primary--text font-weight-medium mr-1"
                  >
                    {{ $t("property.type." + propertyType) }}
                  </v-chip>
                </template>

                <v-chip
                  small
                  color="secondary"
                  class="primary--text font-weight-medium mr-1"
                >
                  {{ $t("operation_type." + search.operationType) }}
                </v-chip>
              </div>

              <div
                v-if="!disabled"
                style="gap: 8px"
                class="d-flex justify-end mt-2"
              >
                <delete :search="search" @reload="$emit('reload')"></delete>
                <edit
                  ref="edit"
                  :search="search"
                  @reload="$emit('reload')"
                ></edit>
              </div>
            </div>

            <v-divider class="mb-3" />

            <v-row>
              <template v-for="field in fields">
                <v-col cols="6" sm="4" lg="3" class="py-0 text--primary">
                  <span class="font-weight-medium">{{
                    $t("filters." + field)
                  }}</span
                  >: {{ parseNumber(search[field]) || "-" }}
                </v-col>
              </template>
            </v-row>
          </v-card-text>
        </v-card>
      </v-card-text>
    </v-card>
  </div>
</template>

<script>
const Edit = () => import("@/components/searches/Edit");
const Delete = () => import("@/components/searches/Delete");
const ShowResults = () => import("@/components/searches/Show");

import { EditResult } from "@/graphql/mutations/results";
import { EditSearch } from "@/graphql/mutations/searches";

export default {
  data: () => ({
    copied: false,
    deleting: false,
    state: null,
    fields: [
      "minPrice",
      "maxPrice",
      "minBrooms",
      "maxBrooms",
      "minRooms",
      "maxRooms",
      "minBaths",
      "maxBaths",
      "minCommonExpenses",
      "maxCommonExpenses"
    ],
    show: false
  }),

  computed: {
    states() {
      if (this.result) {
        return [
          "Pendiente",
          "Esperando respuesta",
          "Visita programada",
          "Visitada",
          "Rechazada",
          "Aprobada"
        ];
      } else {
        return ["Pendiente", "En curso", "Abandonada", "Completada"];
      }
    }
  },

  props: {
    agency: {
      type: Object,
      required: false
    },
    colored: {
      type: Boolean,
      required: false,
      default: false
    },
    disabled: {
      type: Boolean,
      required: false,
      default: false
    },
    result: {
      type: Object,
      required: false
    },
    showHead: {
      required: false,
      default: true,
      type: Boolean
    },
    score: {
      required: false,
      default: null,
      type: String | Number
    },
    search: {
      type: Object,
      required: true
    }
  },

  created() {
    if (this.result) {
      this.state = this.result.state;
    } else {
      this.state = this.search.state;
    }
  },

  methods: {
    openShow() {
      this.$refs.show.dialog = true;
    },

    copyText(text) {
      navigator.clipboard.writeText(text);
      this.copied = true;
    },

    parseNumber(number) {
      let parsed = new Intl.NumberFormat("es-AR").format(number);

      return parsed;
    },

    updateState() {
      if (this.result) {
        this.$apollo
          .mutate({
            mutation: EditResult,
            variables: {
              input: {
                id: this.result.id,
                attributes: {
                  state: this.state
                }
              }
            }
          })
          .then(res => {});
      } else {
        this.$apollo
          .mutate({
            mutation: EditSearch,
            variables: {
              input: {
                id: this.search.id,
                attributes: {
                  state: this.state
                }
              }
            }
          })
          .then(res => {});
      }
    },

    disableResult() {
      this.$apollo
        .mutate({
          mutation: EditResult,
          variables: {
            input: {
              id: this.result.id,
              attributes: {
                dropped: true
              }
            }
          }
        })
        .then(res => {
          this.deleting = false;
          this.$emit("dropped", this.result.id);
        });
    }
  },

  components: {
    Edit,
    ShowResults,
    Delete
  }
};
</script>
